// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { getFocalPointStyles } from "@insite/client-framework/Common/BasicSelectors";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { ColumnAlignment, Option } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridOffset } from "@insite/mobius/GridContainer";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import { Container } from "@rugby/App/Components/Container/Container";
import { screen } from "@rugby/XDL/Helpers";
import systemColors from "@rugby/XDL/PreStyle/colors";
import { color, MediaQuery } from "@rugby/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    border = "border",
    borderSize = "borderSize",
    padding = "padding",
    paddingSize = "paddingSize",
    margin = "margin",
    marginSize = "marginSize",
    breakLine = "breakLine",
    isFullWidth = "isFullWidth",
    fluid = "fluid",
    bgColor = "bgColor",
    alphaColor = "alphaColor",
    fullWidthStylingType = "fullWidthStylingType",
    fullWidthColor = "fullWidthColor",
    fullWidthImage = "fullWidthImage",
    fullWidthImageFocalPoint = "fullWidthImageFocalPoint",
    customCSS = "customCSS",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.border]: boolean;
        [fields.borderSize]: number;
        [fields.padding]: boolean;
        [fields.paddingSize]: string;
        [fields.margin]: boolean;
        [fields.marginSize]: string;
        [fields.breakLine]: boolean;
        [fields.isFullWidth]: boolean;
        [fields.fluid]: boolean;
        [fields.bgColor]: string;
        [fields.alphaColor]: string;
        [fields.fullWidthStylingType]: string;
        [fields.fullWidthColor]: string;
        [fields.fullWidthImage]: string;
        [fields.fullWidthImageFocalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.customCSS]: string;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [12, 6, 6, 3, 3], // <xng-change on xs let the column fill all the available width />
    4: [12, 6, 4, 4, 4], // <xng-change on xs let the column fill all the available width />
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const breakLineStyle = (gap: number) => {
    return css`
        padding-right: ${gap || 0}px;
        position: relative;
        ${screen("lte", "sm")`
            margin-bottom: 20px !important;
        `}

        &:not(:last-child) {
            position: relative;
            &:after {
                content: "";
                width: 2px;
                background: ${color("common.border", 0.6)};
                height: calc(100% - ${gap}px);
                position: absolute;
                right: 0;
                ${MediaQuery.lte("md")} {
                    background: ${color("common.border", 0.6)};
                    width: 100%;
                    height: 2px;
                    position: absolute;
                    bottom: -10px;

                    left: 50%;
                    transform: translateX(-50%);
                }
            }
        }
    `;
};

/**
 * This is a helper method that turn the colors dictionary into dropdown options
 * @param colors
 */
function getAllColorsAsDropdownOptions(colors: any): Option<string>[] {
    const options: Option<string>[] = [];
    for (const key in colors) {
        for (const subkey in colors[key]) {
            if (subkey.toLowerCase().indexOf("contrast") === -1) {
                options.push({ displayName: `${key}.${subkey}`, value: `${key}.${subkey}` });
            }
        }
    }
    return options;
}

// <xng-change force the zone inside the grid item to fill its parent height>
const wrapperCss = css`
    & > div {
        height: 100%;
    }
`;
// <xng-change>

const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            border,
            borderSize,
            padding,
            paddingSize,
            margin,
            marginSize,
            breakLine,
            isFullWidth,
            fluid,
            bgColor,
            alphaColor,
            fullWidthStylingType,
            fullWidthColor,
            fullWidthImage,
            fullWidthImageFocalPoint,
            customCSS,
        },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];
    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem
                key={x}
                width={reflow ? convertToResponsive(columns[x]) : columns[x]}
                align={align}
                css={breakLine ? breakLineStyle(gap || 0) : align === "top" ? wrapperCss : undefined}
            >
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    const fullWidthBackgroundCss = !isFullWidth
        ? undefined
        : fullWidthStylingType === "solidColor"
        ? css`
              background-color: ${fullWidthColor};
          `
        : fullWidthStylingType === "image"
        ? css`
              background-image: url("${parseImageUrl(fullWidthImage).url}");
              background-size: cover;
              ${getFocalPointStyles(fullWidthImageFocalPoint)}
          `
        : undefined;

    const finalCss = css`
        border-color: ${color("common.border", 0.6)};
        border-style: solid;
        border-radius: 3px;
        border-width: ${border ? `${borderSize}px` : 0};
        ${padding && `padding: ${paddingSize}`};

        > div[class^="GridOffset"] {
            ${margin && `margin: ${marginSize};`}
        }
        ${fullWidthBackgroundCss}
        ${customCSS}
    `;

    return (
        <Container
            fullWidth={isFullWidth}
            background={bgColor}
            alphaBackgroundColor={parseFloat(alphaColor) || 1}
            fluid={fluid}
        >
            <GridContainer isFullWidth={isFullWidth} gap={gap || 0} css={finalCss}>
                {columnElements}
            </GridContainer>
        </Container>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const customTab = {
    displayName: "Customization",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: fields.border,
                displayName: "Border",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.borderSize,
                displayName: "Border Size",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                max: 1000,
                min: 1,
                tab: customTab,
                isVisible: item => item.fields.border,
            },
            {
                name: fields.padding,
                displayName: "Padding",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.paddingSize,
                displayName: "Padding Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "20",
                isRequired: true,
                placeholder: "Enter: 5px or any padding style",
                tab: customTab,
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                isVisible: item => item.fields.padding,
            },
            {
                name: fields.margin,
                displayName: "Margin",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.marginSize,
                displayName: "Margin Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0",
                isRequired: true,
                placeholder: "Enter: 5px, 5px auto, ...etc",
                tab: customTab,
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return CSS.supports("margin", value) ? null : `Invalid Input`;
                },
                isVisible: item => item.fields.margin,
            },
            {
                name: fields.breakLine,
                displayName: "BreakLine for children",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.isFullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.fluid,
                displayName: "Fluid Container",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customTab,
                isVisible: item => item.fields.isFullWidth,
            },
            {
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "DropDownField",
                options: getAllColorsAsDropdownOptions(systemColors),
                defaultValue: "",
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.alphaColor,
                displayName: "Color - Opacity",
                editorTemplate: "TextField",
                defaultValue: "1",
                fieldType: "General",
                placeholder: "add color opacity",
                tab: customTab,
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return `A value must be provided.`;
                    }
                    return isNumeric(value) && parseFloat(value) <= 1 && parseFloat(value) > 0 ? null : `Invalid Input`;
                },
            },
        ],
    },
};

export default widgetModule;
